import React, { useState, useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import withStyles from '@material-ui/core/styles/withStyles';
import { Typography, Divider } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import classNames from 'classnames';
import { keyEmptyCheck } from 'aurora-utils/utils';
import { fetchPOAsList } from 'aurora-composition/modules/common/actions';
import { COMMON_REDUCER_CLEAR_ERROR } from 'aurora-composition/modules/common/constants';
import { ADGMResponsiveModal, ADGMAlert, Loading, ADGMHorizontalTabs } from 'aurora-components';
import { FooterButtons } from 'aurora-section-components';
import AddExistingRepresentative from './AddExistingRepresentative';
import AddNewRepresentative from './AddNewRepresentative';
import {
  tabsData,
  tabsValues,
  FETCH_REP_PAGE_SIZE,
  REQUIRED_DOCS,
  REPRESENTATIVE_REQUIRED_KEYS as KEYS,
} from '../constants.js';
import { transformDetails } from './helpers';

import styles from './styles';

function AddRepresentativeModal({
  classes,
  openModal,
  application,
  party,
  representative,
  isPartyTypeCompany,
  addForAllPartiesText,
  documents,
  docsAttributes,
  error,
  closeModalHandler,
  addRepresentativeHandler,
}) {
  const [{ loading: commonLoading, error: commonError }, { locale }] = useSelector(state => [
    state.commonReducer,
    state.language,
  ]);
  const dispatch = useDispatch();

  const isEditFlow = !isEmpty(representative);
  const isExistingRepresentative = representative?.contractId;
  const [activeTab, setActiveTab] = useState(
    !isPartyTypeCompany && isEditFlow && !isExistingRepresentative
      ? tabsValues.ADD_NEW_REPRESENTATIVE
      : tabsValues.EXISTING_REPRESENTATIVE,
  );
  const [existingRepDetails, setExistingRepDetails] = useState(
    isExistingRepresentative ? transformDetails(representative) : {},
  );
  const [newRepDetails, setNewRepDetails] = useState(isExistingRepresentative ? {} : transformDetails(representative));
  const [addRepresentativeForAll, setAddRepresentativeForAll] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isRepDetailsFetched, setIsRepDetailsFetched] = useState(false);
  const [existingRepsData, setExistingRepsData] = useState({});
  const [currentDocuments, setCurrentDocuments] = useState(documents);
  const [newDocuments, setNewDocuments] = useState([]);
  const [deletedDocuments, setDeletedDocuments] = useState([]);

  const nextBtnText = useMemo(() => {
    if (activeTab === tabsValues.EXISTING_REPRESENTATIVE) return 'proceed';
    if (isEditFlow && !isExistingRepresentative && activeTab === tabsValues.ADD_NEW_REPRESENTATIVE)
      return 'save_changes';
    return 'add_poa';
  }, [activeTab, isEditFlow, isExistingRepresentative]);

  useEffect(() => {
    if (activeTab === tabsValues.EXISTING_REPRESENTATIVE) {
      setNewRepDetails(isExistingRepresentative ? {} : transformDetails(representative));
      dispatch(
        fetchPOAsList(application?.applicationId, {
          userId: party?.userId,
          companyId: party?.companyId,
          page: currentPage - 1,
          size: FETCH_REP_PAGE_SIZE,
        }),
      ).then(res => {
        if (!isRepDetailsFetched) setIsRepDetailsFetched(true);
        if (res?.success) {
          setExistingRepsData(res?.result);
        }
      });
    } else {
      setExistingRepDetails(isExistingRepresentative ? transformDetails(representative) : {});
      setIsRepDetailsFetched(false);
      setExistingRepsData({});
      setCurrentPage(1);
      setCurrentDocuments(documents);
      setNewDocuments([]);
      setDeletedDocuments([]);
    }
  }, [activeTab, currentPage]);

  const disableAddRepresentativeBtn = useMemo(() => {
    if (activeTab === tabsValues.EXISTING_REPRESENTATIVE && isEmpty(existingRepDetails)) return true;
    if (activeTab === tabsValues.ADD_NEW_REPRESENTATIVE && isEmpty(newRepDetails)) return true;
    if (activeTab === tabsValues.ADD_NEW_REPRESENTATIVE) {
      const isRequiredDocEmpty = Object.values(REQUIRED_DOCS).some(
        requiredDoc => currentDocuments?.find(doc => doc?.type === requiredDoc) === undefined,
      );
      const isRequiredFieldEmpty = Object.values(KEYS).some(requiredKey => !newRepDetails[requiredKey]);
      return isRequiredDocEmpty || isRequiredFieldEmpty;
    }
    return false;
  }, [existingRepDetails, newRepDetails, currentDocuments, activeTab]);

  const clearErrorIfExists = useCallback(() => {
    if (!isEmpty(commonError)) {
      dispatch({
        type: COMMON_REDUCER_CLEAR_ERROR,
      });
    }
  }, [commonError]);

  const handleChangeRepresentativeForAll = val => {
    setAddRepresentativeForAll(val);
  };

  const handleChangeRepresentativeDetails = (key, value) => {
    setNewRepDetails(curData => ({ ...curData, [key]: value }));
  };

  const handleTabChange = tabVal => {
    setActiveTab(tabVal);
  };

  const handlePageChange = page => {
    setCurrentPage(page);
  };

  const handleCloseModal = () => {
    clearErrorIfExists();
    closeModalHandler();
  };
  const handleSelectExistingRepresentative = rep => {
    clearErrorIfExists();
    setExistingRepDetails(rep);
  };

  const handleAddRepresentative = () => {
    switch (activeTab) {
      case tabsValues.EXISTING_REPRESENTATIVE:
        addRepresentativeHandler({ party, representative: existingRepDetails });
        break;
      case tabsValues.ADD_NEW_REPRESENTATIVE:
        addRepresentativeHandler({
          party,
          representative: newRepDetails,
          addForAll: addRepresentativeForAll,
          newDocs: newDocuments,
          deletedDocs: deletedDocuments,
        });
        break;
      default:
        break;
    }
  };

  const handleAddNewDocument = (body, params) => {
    const formDataObj = Object.fromEntries(body.entries())?.file;
    const document = {
      active: true,
      applicationId: params?.applicationId,
      applicationType: params?.applicationType,
      fileExtension: formDataObj?.type,
      fileName: formDataObj?.name,
      id: params?.documentType,
      subType: params?.subType,
      type: params?.documentType,
      userId: params?.userId ?? null,
      companyId: params?.companyId ?? null,
    };

    setNewDocuments(curDocs => [...curDocs, { body, params }]);
    setCurrentDocuments(curDocs => [...curDocs, document]);
  };

  const handleDeleteDocument = params => {
    const isUploadedDoc = documents?.some(doc => doc?.type === params?.type && doc?.subType === params?.subType);

    if (isUploadedDoc) {
      setDeletedDocuments(curDocs => [...curDocs, params]);
    } else {
      const updatedNewDocs = newDocuments?.filter(
        docData => params?.type !== docData?.params?.documentType || params?.subType !== docData?.params?.subType,
      );
      setNewDocuments(updatedNewDocs);
    }
    const updatedCurrentDocs = currentDocuments?.filter(
      doc => doc?.type !== params?.type || doc?.subType !== params?.subType,
    );
    setCurrentDocuments(updatedCurrentDocs);
  };

  return (
    <ADGMResponsiveModal
      open={openModal}
      toggleModal={handleCloseModal}
      title={keyEmptyCheck(
        activeTab === tabsValues.EXISTING_REPRESENTATIVE ? 'select_representative_poa' : 'add_representative_poa',
      )}
      disablePortal
      disableScrollLock={false}
      customClasses={{
        modal: classes.addBuyerModal,
        header: classes.addBuyerModalHeader,
      }}
    >
      {commonLoading && <Loading />}
      <div className={classes.modalContent}>
        {!isEmpty(error) && (
          <ADGMAlert type='error' customClasses={{ container: classes.errorAlertContainer }}>
            <Typography className={classes.errorAlertMessage}>
              {keyEmptyCheck(error?.message || error?.description || commonError)}
            </Typography>
          </ADGMAlert>
        )}
        {!isEmpty(commonError) && (
          <ADGMAlert type='error' customClasses={{ container: classes.errorAlertContainer }}>
            <Typography className={classes.errorAlertMessage}>
              {keyEmptyCheck(commonError?.message || commonError?.description || commonError)}
            </Typography>
          </ADGMAlert>
        )}
        {!isPartyTypeCompany && <ADGMHorizontalTabs value={activeTab} data={tabsData} onChange={handleTabChange} />}
        <div
          className={classNames({
            [classes.representativeContentWithMarginTop]: !isPartyTypeCompany,
          })}
        >
          {activeTab === tabsValues.EXISTING_REPRESENTATIVE && (
            <AddExistingRepresentative
              locale={locale}
              isEditFlow={isEditFlow && isExistingRepresentative}
              representativesData={existingRepsData}
              selectedRepresentative={existingRepDetails}
              isPartyTypeCompany={isPartyTypeCompany}
              isRepDetailsFetched={isRepDetailsFetched}
              currentPage={currentPage}
              onRepresentativeSelectHandler={handleSelectExistingRepresentative}
              onPageChangeHandler={handlePageChange}
            />
          )}
          {activeTab === tabsValues.ADD_NEW_REPRESENTATIVE && (
            <AddNewRepresentative
              locale={locale}
              isEditFlow={isEditFlow && !isExistingRepresentative}
              application={application}
              representative={newRepDetails}
              addRepresentativeForAll={addRepresentativeForAll}
              addForAllPartiesText={addForAllPartiesText}
              documents={currentDocuments}
              docsAttributes={docsAttributes}
              setRepresentative={setNewRepDetails}
              changeRepresentativeDetailsHandler={handleChangeRepresentativeDetails}
              uploadDocumentHandler={handleAddNewDocument}
              deleteDocumentHandler={handleDeleteDocument}
              changeRepresentativeForAllHandler={handleChangeRepresentativeForAll}
            />
          )}
        </div>
        <Divider className={classes.divider} />
        <FooterButtons
          nextBtnText={nextBtnText}
          previousBtnText='cancel'
          disableNextBtn={disableAddRepresentativeBtn}
          handleNext={handleAddRepresentative}
          handleBack={handleCloseModal}
          customClasses={{ container: classes.footer }}
        />
      </div>
    </ADGMResponsiveModal>
  );
}

AddRepresentativeModal.propTypes = {
  classes: PropTypes.object.isRequired,
  openModal: PropTypes.bool.isRequired,
  application: PropTypes.object.isRequired,
  party: PropTypes.object.isRequired,
  representative: PropTypes.object,
  isPartyTypeCompany: PropTypes.bool.isRequired,
  addForAllPartiesText: PropTypes.string.isRequired,
  documents: PropTypes.array,
  docsAttributes: PropTypes.array,
  error: PropTypes.any.isRequired,
  closeModalHandler: PropTypes.func.isRequired,
  addRepresentativeHandler: PropTypes.func.isRequired,
};

AddRepresentativeModal.defaultProps = {
  representative: {},
  userId: null,
  companyId: null,
  documents: [],
  docsAttributes: [],
};

export default withStyles(styles)(AddRepresentativeModal);
